import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { formatCurrency } from '../../utils/money';
import { invoiceListPath } from '../../services/invoiceList.service';
import { masterproSearchPath } from '../../utils/proSearch';
import { ProTableCell } from '../../components/ProTableCell';
import type { AcctDrillRow, AcctRowType } from '../../services/accountingDashboards.service';

/**
 * One TanTable column set per member type (design doc §6). Column ids are the drill
 * endpoint's SQL aliases (accounting_dashboards.py _DRILL_SQL). Dates carry meta.isDate so
 * TanTable's date filters apply; money renders through formatCurrency. Note invoice_date
 * arrives as a plain date string ('2026-09-09') while delivery_date/created are ISO
 * datetimes — TanTable's date helpers accept both.
 *
 * Three columns are navigational in every member type that has them:
 *   PRO / Template  -> opens the Pro Details drawer over the dashboard (`onOpenPro`).
 *   Master PRO      -> search results for every order on that masterpro, in a NEW TAB.
 *   Invoice (C/V)   -> the List Invoice page for creating or viewing that invoice.
 * Master PRO and C/V go to different places on purpose, which is V1's split: the number
 * itself answers "what is on this masterpro" (fv_masterpro, formatview.pl:3256) and the
 * letter answers "invoice it" (fv_listinvoice, :2355). Both open a new tab and leave this
 * page alone — coming back to a drill you had filtered and scrolled should not cost a
 * reload. They are built here rather than in the card so every bucket gets them from one
 * definition — a column set that is missing a link is the bug this shape prevents.
 */
type Col = ColumnDef<AcctDrillRow>;

/** The drill card supplies this; columns are rebuilt when it changes identity. */
export interface DrillColumnHandlers {
  /** Templates ride the same drawer as numeric PROs, hence `number | string`. */
  onOpenPro: (pronumber: number | string) => void;
  /** Opens one additional-cost record (AdditionalCostItemModal) straight from an
   *  Accounts Payable row, rather than by way of the order's Addl Cost tab. */
  onOpenAddlCost: (pronumber: number, recno: number) => void;
}

const LINK_CLASS = 'text-blue-600 dark:text-blue-400 hover:underline tabular-nums';

/** Viewed-tracking namespace for the C/V invoice link — see the cell for why it is not
 *  the default PRO group. */
export const VIEWED_INVOICE_GROUP = 'invoice';

/** The drill columns whose values are PROs, for TanTable's green viewed-tracking. */
export const VIEWED_PRO_COLUMNS = ['pronumber', 'template_pro'];

/**
 * V1 colours the Master PRO cell by the masterpro's margin and trouble-ticket state
 * (fv_masterpro, formatview.pl:3268-3279). Solid backgrounds that repeat in dark mode on
 * purpose — like the Pro Details flag treatment, a highlight that dims stops being one.
 *
 * Red is a separate `if` applied AFTER the margin test in V1, so tickets win.
 *
 * Green is an EXACT match on 'APPROVED', not a contains. The value is concatenated across
 * every pro on the masterpro, and a pro that is not thin-margin contributes an empty
 * string — so one ordinary pro turns ',APPROVED' and the row shows no colour at all. On
 * prod that is 21 of 229 Pending Invoicing masterpros. It reads like an accident, but it
 * is what V1 does, and matching it is what lets the two screens be compared.
 */
export type MasterProTone = 'red' | 'orange' | 'green' | null;

const TONE_CLASS: Record<Exclude<MasterProTone, null>, string> = {
  red: 'bg-red-300 dark:bg-red-300 text-black px-1 rounded',
  orange: 'bg-orange-300 dark:bg-orange-300 text-black px-1 rounded',
  green: 'bg-green-300 dark:bg-green-300 text-black px-1 rounded',
};

const TONE_TITLE: Record<Exclude<MasterProTone, null>, string> = {
  red: 'PENDING TROUBLE TICKETS',
  orange: 'PENDING MARGIN APPROVAL',
  green: 'MARGIN APPROVED',
};

export function masterProTone(row: AcctDrillRow): MasterProTone {
  if (Number(row.open_trouble_tickets) > 0) return 'red';
  const approval = row.marginapproval === null || row.marginapproval === undefined
    ? '' : String(row.marginapproval);
  if (approval.includes('PENDING')) return 'orange';
  if (approval === 'APPROVED') return 'green';
  return null;
}

const money = (accessorKey: string, header: string): Col => ({
  accessorKey,
  header,
  size: 120,
  cell: ({ getValue }) => formatCurrency(getValue()),
  meta: { aggType: 'sum' },
});

const day = (accessorKey: string, header: string): Col => ({
  accessorKey, header, size: 130, meta: { isDate: true, showTime: false },
});

/** A date column that also shows the clock time when one was recorded — for
 *  senttoaccountingdate, which carries a real time since migration 487. */
const dayTime = (accessorKey: string, header: string): Col => ({
  accessorKey, header, size: 150, meta: { isDate: true, hideMidnightTime: true },
});

const txt = (accessorKey: string, header: string, size = 160): Col => ({ accessorKey, header, size });

/**
 * The bill-to's billing cadence (cms.billtos.frequency — "Invoice Frequency" on the bill-to
 * drawer). Hidden by default in every bucket and added from the column picker, like the rest
 * of what V1's layouts do not draw: the clerks asked to see, while deciding what to invoice
 * now, that a pro is ready but its bill-to only bills monthly.
 *
 * Only on the shapes where that decision is made — pro, invoice and the two masterpro
 * groupings. A template is not invoiceable and an addl-cost row is a payable, so the
 * customer's billing cadence is noise on both.
 */
const invoiceFrequency = (): Col => txt('invoice_frequency', 'Invoice Frequency', 140);

const count = (accessorKey: string, header: string, size = 90): Col => ({
  accessorKey, header, size, meta: { aggType: 'sum', align: 'right' },
});

/** PRO (or Template) as a button that opens the Pro Details drawer in place. */
const pro = (handlers: DrillColumnHandlers, accessorKey: string, header: string, size = 110): Col => ({
  accessorKey,
  header,
  size,
  cell: ({ getValue }) => {
    const value = getValue();
    const text = value === null || value === undefined ? '' : String(value);
    if (!text) return '';
    return (
      <button
        type="button"
        className={LINK_CLASS}
        title="Open the order"
        // Numeric PROs go through as numbers, the way every other caller opens the
        // drawer; a template's 'T64437' stays a string.
        onClick={() => handlers.onOpenPro(/^\d+$/.test(text) ? Number(text) : text)}
      >
        {text}
      </button>
    );
  },
});

/**
 * Master PRO as a link to search results for every order on it — V1's `fv_masterpro`.
 * Invoicing lives on the C/V link beside it, so this one answers the other question:
 * which orders are on this masterpro.
 */
const masterPro = (): Col => ({
  accessorKey: 'masterpro',
  header: 'Master PRO',
  size: 120,
  cell: ({ getValue, row }) => {
    const value = getValue();
    const text = value === null || value === undefined ? '' : String(value);
    if (!text) return '';
    // Only the Pending Invoicing family carries these; every other drill leaves them
    // absent and the cell is simply uncoloured, which is V1's behaviour too.
    const tone = masterProTone(row.original);
    return (
      <a
        className={`${LINK_CLASS} ${tone ? TONE_CLASS[tone] : ''}`}
        href={masterproSearchPath(text)}
        target="_blank"
        rel="noopener noreferrer"
        title={`Orders on this Master PRO (new tab)${tone ? `\n${TONE_TITLE[tone]}` : ''}`}
      >
        {text}
      </a>
    );
  },
});

/**
 * The additional-cost recno, as a link that opens that cost record itself
 * (AdditionalCostItemModal — the same editor the order's Addl Cost tab opens). The
 * Accounts Payable boards are where a clerk works a cost, and until now the recno was
 * just a number to go hunting for.
 *
 * Needs the row as well as the value: the modal is keyed on (pronumber, recno). A row
 * without a usable numeric pro renders as plain text rather than a dead link.
 */
const costRecno = (handlers: DrillColumnHandlers): Col => ({
  accessorKey: 'addlcost_recno',
  header: 'Cost Recno',
  size: 110,
  cell: ({ getValue, row }) => {
    const value = getValue();
    const text = value === null || value === undefined ? '' : String(value);
    if (!text) return '';
    const recno = Number(text);
    const pronumber = Number((row.original as { pronumber?: unknown }).pronumber);
    // > 0, not isFinite: Number(null) and Number('') are both 0, which is finite —
    // a row with a null pro would render a live link that opens the modal on pro 0.
    if (!(recno > 0) || !(pronumber > 0)) return text;
    return (
      <button
        type="button"
        className={LINK_CLASS}
        title="Open the additional cost"
        onClick={() => handlers.onOpenAddlCost(pronumber, recno)}
      >
        {text}
      </button>
    );
  },
});

const proColumns = (h: DrillColumnHandlers): Col[] => [
  pro(h, 'pronumber', 'PRO'), masterPro(), txt('container', 'Container', 130),
  txt('movetype', 'Move Type', 110), txt('billto_name', 'Bill To', 200), txt('customer_name', 'Customer', 200),
  day('delivery_date', 'Delivered'), txt('billfromsystem', 'Bill From', 110), day('invoice_date', 'Invoice Date'),
  money('invoice_net', 'Invoice Net'), txt('donotbillreason', 'DNB Reason', 140), txt('pendingapproval', 'Pending Appr.', 110),
  txt('csr', 'CSR', 110), txt('dispatcher', 'Dispatcher', 110), txt('region', 'Region', 110),
  invoiceFrequency(),
  // Everything V1's eleven per-bucket layouts draw on. Which of these a given bucket
  // SHOWS is V1_BUCKET_LAYOUT below; the rest stay addable from the column picker.
  txt('sage_tag', 'Sage Tag', 90), txt('bol', 'BOL/BKG/Ref#', 180), txt('vessel', 'Vessel', 140),
  txt('sonumber', 'SO Number', 120), day('lastfreedaydate', 'Last Free Day'),
  day('perdiemstartdate', 'Per Diem Start'), day('created', 'Created'),
  dayTime('sent_to_accounting', 'Sent Acct'), day('masexportdate', 'Sage Export Date'),
  listInvoice(),
];

const invoiceColumns = (h: DrillColumnHandlers): Col[] => [
  pro(h, 'pronumber', 'PRO'), txt('accessorialnum', 'Acc #', 80), masterPro(), txt('container', 'Container', 130),
  txt('movetype', 'Move Type', 110), txt('billto_name', 'Bill To', 200), txt('customer_name', 'Customer', 200),
  txt('line_items', 'Line Items', 220), txt('billfromsystem', 'Bill From', 110), day('created', 'Created'),
  day('invoice_date', 'Invoice Date'), money('invoice_net', 'Invoice Net'), txt('donotbillreason', 'DNB Reason', 140),
  txt('pendingapproval', 'Pending Appr.', 110), txt('invoice_recno', 'Invoice Recno', 110),
  txt('csr', 'CSR', 110), txt('sage_tag', 'Sage Tag', 90), txt('bol', 'BOL/BKG/Ref#', 180),
  // THIS accessorial's own customer reference, not the base invoice's — each row here is
  // one cms.invoice record. 7,441 of the 16,112 rows in acct-acc-total carry one, and it
  // is the number the customer's AP department matches the charge against.
  txt('billtorefno', 'Bill To Ref #', 150),
  invoiceFrequency(),
  txt('division', 'Division', 120), day('delivery_date', 'Delivered'), dayTime('sent_to_accounting', 'Sent Acct'),
  listInvoice({ perInvoice: true }),
];

const addlcostColumns = (h: DrillColumnHandlers): Col[] => [
  pro(h, 'pronumber', 'PRO'), masterPro(), txt('container', 'Container', 130), txt('billto_name', 'Bill To', 200),
  txt('customer_name', 'Customer', 200), txt('vendor_name', 'Vendor', 200), txt('costtype', 'Cost Type', 150),
  txt('status', 'Status', 100), txt('hold_type', 'Hold Type', 150), txt('invoicenumber', 'Vendor Invoice #', 140),
  day('invoice_date', 'Invoice Date'), money('invoicetotal', 'Invoice Total'), day('created', 'Created'),
  txt('comments', 'Comments', 260), costRecno(h),
];

// A template has no masterpro and no invoice; its T-pronumber opens the same drawer.
const templateColumns = (h: DrillColumnHandlers): Col[] => [
  pro(h, 'template_pro', 'Template', 120), txt('templatetype', 'Type', 120), txt('movetype', 'Move Type', 110),
  txt('equiptype', 'Equipment', 120), txt('billto_name', 'Bill To', 200), txt('customer_name', 'Customer', 200),
  txt('shippername', 'Shipper', 200), txt('createdby', 'Created By', 120), day('created', 'Created'),
];

/**
 * V1's `listinvoice` column (formatview.pl::fv_listinvoice): a single letter linking to the
 * List Invoice page. **C** = Create Invoice, for a row with no invoice history yet; **V** =
 * View Invoice, carrying the invoice history recno. Creation lives on that page, not here —
 * the one exception is the accessorial buckets, where V1 renders per-invoice checkboxes
 * instead and our drill keeps its Create Invoices button.
 *
 * `perInvoice` says the row IS one invoice rather than an order or a masterpro, which is
 * true of every accessorial drill (all 26 `member_type='invoice'` buckets: `acct-acc-*`,
 * `acct-acchold-*`, `acct-approval-acc-*`, `acct-past-due-accessorial`, `acct-pinv-*`).
 * There, C carries that invoice's own `invoice.recno` as `invoicerecnos` — carrying the
 * masterpro instead opened every invoice on it rather than the accessorial you clicked
 * (#33683). The backend resolves an explicit recno selection ahead of an invoice number
 * and derives Bill To from the invoice row itself, so the recno is all the link needs.
 */
const listInvoice = (opts?: { perInvoice?: boolean }): Col => ({
  id: 'listinvoice',
  header: 'Invoice',
  size: 70,
  enableSorting: false,
  accessorFn: (row) => (Number(row.invhistoryrecno) > 0 ? 'V' : 'C'),
  cell: ({ row }) => {
    const history = Number(row.original.invhistoryrecno) || 0;
    const masterpro = String(row.original.masterpro ?? '');
    const billto = Number(row.original.invoice_billto) || 0;
    const invoiceRecno = opts?.perInvoice ? Number(row.original.invoice_recno) || 0 : 0;
    if (history <= 0 && !masterpro && invoiceRecno <= 0) return '';
    const params = history > 0
      ? { invhistoryrecno: history }
      : invoiceRecno > 0
        ? { invoicerecnos: String(invoiceRecno) }
        : (billto > 0 ? { masterpro, billto } : { masterpro });
    return (
      // Green once opened, the standard PRO treatment — but in its OWN namespace, so it
      // never cross-highlights with the PRO column or steals its last-clicked ring. A
      // masterpro IS a pro number in this system, so sharing the default group would make
      // opening an order light up an unrelated invoice link.
      //
      // Keyed by what the link actually OPENS, so "visited" means what it says. On the
      // masterpro/pro drills that is the masterpro, and keying it there rather than by row
      // is deliberate — every row of a masterpro you have opened reads as done. On the
      // accessorial drills it is the one invoice, because keying those by masterpro would
      // green out every sibling accessorial the moment you opened any one of them.
      <ProTableCell
        pro={
          invoiceRecno > 0 && history <= 0
            ? `invrec-${invoiceRecno}`
            : masterpro || `inv-${history}`
        }
        group={VIEWED_INVOICE_GROUP}
      >
        <a
          className={LINK_CLASS}
          href={invoiceListPath(params)}
          target="_blank"
          rel="noopener noreferrer"
          title={history > 0 ? 'View Invoice (new tab)' : 'Create Invoice (new tab)'}
        >
          {history > 0 ? 'V' : 'C'}
        </a>
      </ProTableCell>
    );
  },
  meta: { align: 'center' },
});

/**
 * Pending Invoicing — one row per MASTERPRO (searchresults.pl:388). Bill To, Sage tag and
 * Sent Acct are the representative pro's; BOL, Move Type, the dates and the container count
 * are aggregated across every order on the masterpro.
 *
 * Deliberate divergence: V1 shows no dollar column here, only a Grand Total under the grid.
 * We carry the same number per row and let TanTable's sum footer produce that total, which
 * is strictly more information and cannot disagree with it.
 */
const masterproColumns = (): Col[] => [
  masterPro(), txt('billto_name', 'Bill To', 200), txt('sage_tag', 'Sage Tag', 90), invoiceFrequency(),
  txt('bol', 'BOL/BKG/Ref#', 180), txt('movetype', 'Move Type', 130),
  day('delivery_date', 'Delivery Date'), day('terminate_date', 'Terminate Date'),
  dayTime('sent_to_accounting', 'Sent Acct'), listInvoice(), count('container_count', '# of Containers', 120),
  money('invoice_net', 'Invoice Net'),
];

/**
 * Invoicing Hold — searchresults.pl:448. NOT a full aggregation: V1 keeps its default
 * GROUP BY pronumber here, so BOL, Move Type, Region and the dates stay the representative
 * pro's and only the container count and the dollar total reach across the masterpro.
 */
const masterproHoldColumns = (): Col[] => [
  masterPro(), txt('division', 'Division', 120), txt('billto_name', 'Bill To', 200),
  txt('sage_tag', 'Sage Tag', 90), invoiceFrequency(), txt('bol', 'BOL/BKG/Ref#', 180),
  txt('movetype', 'Move Type', 110),
  txt('region', 'Region', 110), day('delivery_date', 'Delivery Date'), day('terminate_date', 'Terminate Date'),
  dayTime('sent_to_accounting', 'Sent Acct'), listInvoice(), count('container_count', '# of Containers', 120),
  money('invoice_net', 'Invoice Net'),
];

/**
 * What V1 shows in each bucket, in V1's order.
 *
 * `doshowbucketacct` (searchresults.pl:277) keys ELEVEN `@search_layout`s off the bucket
 * name, and `doshowbucketacctaccessorial` (:692) another ten. A bucket listed here shows
 * exactly those columns by default; everything else in its set is hidden but still
 * addable from the column picker, because our tables let a user keep a column V1 never
 * offered and taking that away would be a downgrade, not parity.
 *
 * A bucket NOT listed here keeps the full set visible — that is every bucket V1 leaves to
 * its own default layout (15 Days Past Due, the Approval buckets, payables, templates).
 */
export const V1_BUCKET_LAYOUT: Record<string, string[]> = {};

const layout = (keys: string[], columns: string[]) => {
  keys.forEach((k) => { V1_BUCKET_LAYOUT[k] = columns; });
};

// --- doshowbucketacct ----------------------------------------------------------------
const CMS_BASE = ['billto_name', 'csr', 'sage_tag'];
const CMS_TAIL = ['pronumber', 'container', 'movetype', 'bol', 'sent_to_accounting', 'invoice_net', 'listinvoice'];

// 'Not Invoiced'
layout(['acct-cms-not-invoiced'], [...CMS_BASE, 'created', 'delivery_date', ...CMS_TAIL]);
// 'Delivered' and, with adjinvoicetotal (which our invoice_net already is), 'Invoice Amt'
// / 'No Invoice Amt' / 'Invoiced' / 'CMS' / 'Bill From System'.
layout(['acct-cms-delivered', 'acct-cms-delivered-inv-gt0', 'acct-cms-delivered-inv-eq0',
        'acct-cms-invoiced', 'acct-cms-total', 'acct-total-bill-from-system'],
       [...CMS_BASE, 'delivery_date', 'invoice_date', ...CMS_TAIL]);
// 'Not Delivered' / 'ND No Invoice Amt'
layout(['acct-cms-not-delivered', 'acct-cms-not-delivered-inv-eq0'],
       [...CMS_BASE, 'created', 'invoice_date', ...CMS_TAIL]);
// 'ND Invoice Amt' swaps the invoice date for the two per-diem dates.
layout(['acct-cms-not-delivered-inv-gt0'],
       [...CMS_BASE, 'created', 'lastfreedaydate', 'perdiemstartdate', ...CMS_TAIL]);
// 'DO NOT BILL' / 'DNB *' — note V1 offers no C/V link on these.
layout(['acct-approval-donotbill', 'acct-approval-donotbill-not-approved',
        'acct-approval-donotbill-researching'],
       [...CMS_BASE, 'pronumber', 'donotbillreason', 'container', 'movetype', 'bol',
        'sent_to_accounting', 'invoice_net']);

// --- doshowbucketacctaccessorial -----------------------------------------------------
const ACC = ['billto_name', 'csr', 'sage_tag', 'created', 'delivery_date', 'pronumber',
             'accessorialnum', 'container', 'movetype', 'bol', 'sent_to_accounting',
             'invoice_net', 'listinvoice'];
layout(['acct-pinv-accessorials', 'acct-pinv-per-diem', 'acct-pinv-chassis',
        'acct-acc-total', 'acct-acc-not-invoiced', 'acct-acc-not-invoiced-per-diem',
        'acct-acc-delivered', 'acct-acc-delivered-inv-gt0', 'acct-acc-delivered-inv-eq0',
        'acct-acc-not-delivered', 'acct-acc-not-delivered-inv-gt0', 'acct-acc-not-delivered-inv-eq0',
        'acct-acc-invoiced', 'acct-acc-invoiced-per-diem'], ACC);
// The Acc On Hold family inserts Division after the Bill To.
layout(['acct-acchold-total', 'acct-acchold-revised-do', 'acct-acchold-pod-docs',
        'acct-acchold-edi', 'acct-acchold-pending-approval', 'acct-acchold-margin',
        'acct-acchold-trouble-ticket', 'acct-acchold-missing-driver-pay-rate'],
       ['billto_name', 'division', ...ACC.slice(1)]);

/**
 * Which columns a bucket hides by default. Empty when V1 has no layout for it, which
 * leaves every column visible.
 */
/**
 * Columns 360 offers that no V1 layout ever drew. A bucket V1 has no layout for shows its
 * whole set, so without this these would appear by default on exactly the screens we have
 * no layout to measure them against — including Pending Invoicing, which is where the
 * Invoice Frequency column is most wanted. Hidden everywhere, addable everywhere.
 */
const ADDED_BEYOND_V1 = new Set(['invoice_frequency', 'billtorefno']);

export function drillVisibilityFor(bucketKey: string, rowType: AcctRowType,
                                   handlers: DrillColumnHandlers): Record<string, boolean> {
  const wanted = V1_BUCKET_LAYOUT[bucketKey];
  const hidden: Record<string, boolean> = {};
  BUILDERS[rowType](handlers).forEach((col) => {
    const id = (col as { accessorKey?: string; id?: string }).accessorKey
      ?? (col as { id?: string }).id;
    if (!id) return;
    // No V1 layout for this bucket: leave it as V1 draws it, minus anything V1 never had.
    if (!wanted) {
      if (ADDED_BEYOND_V1.has(id)) hidden[id] = false;
      return;
    }
    if (!wanted.includes(id)) hidden[id] = false;
  });
  return hidden;
}

const BUILDERS: Record<AcctRowType, (h: DrillColumnHandlers) => Col[]> = {
  pro: proColumns, invoice: invoiceColumns, addlcost: addlcostColumns, template: templateColumns,
  masterpro: masterproColumns, masterpro_hold: masterproHoldColumns,
};

export const drillColumnsFor = (rowType: AcctRowType, handlers: DrillColumnHandlers): Col[] =>
  BUILDERS[rowType](handlers);
